import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import AddMember from "@/pages/AddMember";
import PhoneLookup from "@/pages/PhoneLookup";
import Departed from "@/pages/Departed";
import MemberDetail from "@/pages/MemberDetail";
import GlobalHistory from "@/pages/History";
import DilekceList from "@/pages/DilekceList";
import Devirs from "@/pages/Devirs";
import AccessAttempts from "@/pages/AccessAttempts";
import MemberVisits from "@/pages/MemberVisits";
import MemberLogin from "@/pages/member/MemberLogin";
import MemberRegister from "@/pages/member/MemberRegister";
import MemberPortal from "@/pages/member/MemberPortal";
import MemberProfile from "@/pages/member/MemberProfile";
import { MemberProtected } from "@/pages/member/MemberShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate("/login"); return; }
    const sid = m[1];
    (async () => {
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        await axios.post(`${API}/auth/session`, { session_id: sid }, { withCredentials: true });
        window.history.replaceState({}, document.title, "/");
        navigate("/", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "";
        if (e?.response?.status === 403) {
          setError(msg || "Bu Google hesabı yönetici paneline erişim için yetkili değildir.");
          window.history.replaceState({}, document.title, "/login");
          return;
        }
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 shadow-md text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-50 border border-red-200 text-red-600 flex items-center justify-center text-2xl font-bold mb-3">!</div>
          <h2 className="text-lg font-bold text-slate-900 mb-1">Yetkisiz Google Hesabı</h2>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <button onClick={() => (window.location.href = "/login")}
            className="w-full rounded-md bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800">
            Giriş Ekranına Dön
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-600 text-sm">Giriş yapılıyor...</div>
    </div>
  );
}

function AdminProtected({ children }) {
  const [state, setState] = useState({ loading: true, user: null });
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setState({ loading: false, user: r.data });
      } catch {
        setState({ loading: false, user: null });
      }
    })();
  }, []);
  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">Yükleniyor...</div>;
  }
  if (!state.user) return <Navigate to="/login" replace />;
  return children({ user: state.user });
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Public member routes */}
      <Route path="/uye/giris" element={<MemberLogin />} />

      {/* Member protected portal */}
      <Route path="/uye" element={<MemberProtected><MemberPortal /></MemberProtected>} />
      <Route path="/uye/profil" element={<MemberProtected><MemberProfile /></MemberProtected>} />

      {/* Admin */}
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={
        <AdminProtected>
          {({ user }) => (
            <AppShell user={user}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/ortaklar" element={<Members />} />
                <Route path="/yeni-uye" element={<AddMember />} />
                <Route path="/telefon" element={<PhoneLookup />} />
                <Route path="/ayrilan" element={<Departed />} />
                <Route path="/gecmis" element={<GlobalHistory />} />
                <Route path="/devirler" element={<Devirs />} />
                <Route path="/ziyaretler" element={<MemberVisits />} />
                <Route path="/erisim" element={<AccessAttempts />} />
                <Route path="/dilekceler" element={<DilekceList />} />
                <Route path="/ortak/:id" element={<MemberDetail />} />
              </Routes>
            </AppShell>
          )}
        </AdminProtected>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <AppRouter />
    </BrowserRouter>
  );
}
